import { ChevronLeftIcon } from '@chakra-ui/icons';
import { Box, Flex, Text, keyframes, useBoolean, useMediaQuery } from '@chakra-ui/react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FiMoreHorizontal, FiX } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import DuplicateIconV1 from 'components/Icons/DuplicateIconV1';
import FigmaEditIcon from 'components/Icons/FigmaEditIcon';
import FigmaTrashIcon from 'components/Icons/FigmaTrashIcon';
import DeleteModal from 'components/Modals/DeleteModal';
import {
  PLAN_HEADER_HORIZONTAL_PADDING,
  PLAN_REVAMP_RADIUS_SM,
  PLAN_TABLE_CELL_BORDER,
  PLAN_TEXT,
} from 'pages/PlanRevamp/PlanPageUIRevamp/planPageRevampUi';
import {
  clearStepperFormData,
  deleteDataImportFile,
  duplicateSourceFile,
  editDataImportFile,
  getDataImportFiles,
  getImportDataFiles,
  getSelectedDataFileDetails,
  getVersionList,
  resetDataImport,
  setActionCreateForm,
  setClearSelectedAreasTable,
} from 'redux/DataImportSlice';
import { handleFileDelete } from 'services/s3Service';
import { fontSize } from 'styles/revampTokens';
import { AppDispatch } from 'utils/GlobalHelpers';

import ActionsBar from './ActionsBar';
import ActionCreateForm from './ActionSteps/ActionCreateForm';
import AddVersionModal from './AddVersionModal';
import colors from './dataTheme';
import DataPreview from './DataPreview';
import { SectionTitle, cardStyle, iconButtonStyle } from './detailUi';
import DiscardActionModal from './DiscardActionModal';
import useLeaveGuard from './useLeaveGuard';
import EditSourceModal from './EditSourceModal';
import { formatCreatedOn, getTypeLabel, type SourceFile } from './sourcesTableUtils';
import VersionsPanel from './VersionsPanel';

interface Properties {
  actionCreate: boolean;
  setActionCreate: (value: boolean) => void;
}

const zoomIn = keyframes`
  from { opacity: 0; transform: scale(0.92); }
  to { opacity: 1; transform: scale(1); }
`;
const zoomOut = keyframes`
  from { opacity: 1; transform: scale(1); }
  to { opacity: 0; transform: scale(0.92); }
`;
const ENTER_MS = 350;
const EXIT_MS = 300;

const SourceDetail = ({ actionCreate, setActionCreate }: Properties) => {
  // The create view stays mounted for the exit animation after `isCreateView` goes false.
  const [isExiting, setIsExiting] = useState(false);
  const [isCreateView, setIsCreateView] = useState(actionCreate);

  const dispatch: AppDispatch = useDispatch();
  const { modelId } = useParams();
  const selectedFileDetails = useSelector(getSelectedDataFileDetails);
  const importFiles: SourceFile[] | null = useSelector(getImportDataFiles);
  const versionList = useSelector(getVersionList);
  const [versionModal, setVersionModal] = useBoolean();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const [isCompact] = useMediaQuery('(max-width: 1279px)');
  const [isMobile] = useMediaQuery('(max-width: 1156px)');
  const [isSmallLaptop] = useMediaQuery('(max-width: 1270px)');

  // Fill whatever viewport height is left below the header so the sidebar and the
  // data workspace are full height and scroll internally.
  const bodyReference = useRef<HTMLDivElement>(null);
  const [bodyHeight, setBodyHeight] = useState(560);
  useLayoutEffect(() => {
    const measure = () => {
      const top = bodyReference.current?.getBoundingClientRect().top ?? 0;
      setBodyHeight(Math.max(480, window.innerHeight - top - 16));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [actionCreate]);

  const dataSourceId = selectedFileDetails?.id;
  const source = importFiles?.find((item) => String(item.id) === String(dataSourceId));
  const sourceName = source?.file_name ?? selectedFileDetails?.name ?? '';
  const typeLabel = source ? getTypeLabel(source) : 'CSV';
  const versions: any[] = versionList?.versions ?? [];

  const refetchSources = () => dispatch(getDataImportFiles(modelId));

  const saveName = async (name: string) => {
    if (!dataSourceId) return;
    await dispatch(
      editDataImportFile({
        modelId,
        fileId: dataSourceId,
        request_body: { file_name: name, file_type: 'csv' },
        toaster: {
          successMessage: 'Successfully updated',
          errorMessage: 'An Source with this name already exists',
        },
      }),
    );
    setIsEditOpen(false);
    refetchSources();
  };

  const duplicateSource = async () => {
    if (!dataSourceId) return;
    await dispatch(duplicateSourceFile(dataSourceId));
    refetchSources();
  };

  const confirmDelete = async () => {
    if (!dataSourceId) return;
    await handleFileDelete(sourceName, 'blox-data-import');
    await dispatch(deleteDataImportFile({ modelId, fileId: dataSourceId }));
    setIsDeleteOpen(false);
    await refetchSources();
    dispatch(resetDataImport());
  };

  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const sourceActions = [
    {
      key: 'rename',
      label: 'Rename',
      icon: <FigmaEditIcon width={14} height={14} color={colors.textDark} />,
      color: colors.textDark,
      hoverBg: colors.hover,
      onSelect: () => setIsEditOpen(true),
    },
    {
      key: 'duplicate',
      label: 'Duplicate',
      icon: <DuplicateIconV1 size={14} />,
      color: colors.textDark,
      hoverBg: colors.hover,
      onSelect: duplicateSource,
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <FigmaTrashIcon width={14} height={14} color={colors.danger} />,
      color: colors.danger,
      hoverBg: `${colors.danger}15`,
      onSelect: () => setIsDeleteOpen(true),
    },
  ];

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (actionCreate) {
      setIsCreateView(true);
      setIsExiting(false);
    } else if (isCreateView) {
      setIsExiting(true);
      timer = setTimeout(() => {
        setIsExiting(false);
        setIsCreateView(false);
        // Clear only once the exit animation is done so the form does not reset mid-fade.
        dispatch(clearStepperFormData());
        dispatch(setClearSelectedAreasTable());
      }, EXIT_MS);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [actionCreate]);

  const isEditingAction = useSelector((state: any) => state.dataImportSlice.isEdit?.isEditForm);

  const [isDiscardOpen, setIsDiscardOpen] = useState(false);
  const activeActionStep: number = useSelector((state: any) => state.dataImportSlice.actionSteps);

  const closeCreateForm = () => {
    setActionCreate(false);
    dispatch(setActionCreateForm(false));
  };

  // Leaving after the first step would silently throw the wizard's progress away.
  const requestCloseCreateForm = () => {
    if (activeActionStep > 0) setIsDiscardOpen(true);
    else closeCreateForm();
  };

  // Route changes (top nav, browser back) and tab close are guarded the same way as the ✕.
  const pendingNavigation = useRef<(() => void) | null>(null);
  useLeaveGuard(isCreateView && activeActionStep > 0, (retry) => {
    pendingNavigation.current = retry;
    setIsDiscardOpen(true);
  });

  const renderHeader = () => (
    <Box w='100%' py='12px' flexShrink={0}>
      {!isCreateView && (
        <Flex
          as='button'
          type='button'
          alignItems='center'
          gap='2px'
          mb='6px'
          cursor='pointer'
          color={colors.textSecondary}
          transition='color 0.12s ease'
          _hover={{ color: colors.primary }}
          onClick={isCreateView ? requestCloseCreateForm : () => dispatch(resetDataImport())}
        >
          <ChevronLeftIcon boxSize={4} />
          <Text {...PLAN_TEXT.labelSmMedium}>
            {isCreateView ? 'Back to Actions' : 'Back to Sources'}
          </Text>
        </Flex>
      )}

      <Flex alignItems='center' justifyContent='space-between' gap={4}>
        <Box minW={0}>
          <Text
            {...PLAN_TEXT.pageTitle}
            color={colors.textDark}
            noOfLines={1}
            title={isCreateView ? undefined : sourceName}
          >
            {isCreateView ? (isEditingAction ? 'Edit action' : 'Create new action') : sourceName}
          </Text>
        </Box>

        {isCreateView && (
          <Flex
            as='button'
            type='button'
            aria-label='Close'
            title='Close'
            {...iconButtonStyle}
            onClick={requestCloseCreateForm}
          >
            <FiX size={18} />
          </Flex>
        )}

        <Flex alignItems='center' gap='6px' flexShrink={0} display={isCreateView ? 'none' : 'flex'}>
          {sourceActions.map((item, index) => (
            <Flex
              key={item.key}
              as='button'
              type='button'
              alignItems='center'
              justifyContent='center'
              gap='6px'
              h='32px'
              flexShrink={0}
              overflow='hidden'
              whiteSpace='nowrap'
              border='1px solid'
              borderColor={isActionsOpen ? colors.border : 'transparent'}
              borderRadius={PLAN_REVAMP_RADIUS_SM}
              bg={colors.surface}
              color={item.color}
              fontSize={fontSize.body}
              fontWeight={500}
              cursor='pointer'
              pointerEvents={isActionsOpen ? 'auto' : 'none'}
              tabIndex={isActionsOpen ? 0 : -1}
              maxW={isActionsOpen ? '140px' : '0px'}
              px={isActionsOpen ? '12px' : '0px'}
              ml={isActionsOpen ? 0 : '-6px'}
              opacity={isActionsOpen ? 1 : 0}
              transform={isActionsOpen ? 'translateX(0)' : 'translateX(12px)'}
              transition='max-width 0.28s ease, padding 0.28s ease, margin 0.28s ease, opacity 0.2s ease, transform 0.28s ease, background 0.12s ease'
              transitionDelay={isActionsOpen ? `${index * 40}ms` : `${(2 - index) * 30}ms`}
              _hover={{ bg: item.hoverBg }}
              onClick={() => {
                setIsActionsOpen(false);
                item.onSelect();
              }}
            >
              {item.icon}
              {item.label}
            </Flex>
          ))}

          <Flex
            as='button'
            type='button'
            aria-label={isActionsOpen ? 'Close source actions' : 'Source actions'}
            aria-expanded={isActionsOpen}
            position='relative'
            alignItems='center'
            justifyContent='center'
            w='32px'
            h='32px'
            flexShrink={0}
            border='1px solid'
            borderColor={isActionsOpen ? colors.primary : colors.border}
            borderRadius={PLAN_REVAMP_RADIUS_SM}
            bg={isActionsOpen ? colors.activeBg : colors.surface}
            color={isActionsOpen ? colors.primary : colors.textDark}
            cursor='pointer'
            transition='background 0.2s ease, border-color 0.2s ease, color 0.2s ease'
            _hover={{ bg: isActionsOpen ? colors.activeBg : colors.hover }}
            onClick={() => setIsActionsOpen((open) => !open)}
          >
            <Box
              position='absolute'
              display='flex'
              transition='transform 0.28s ease, opacity 0.2s ease'
              opacity={isActionsOpen ? 0 : 1}
              transform={isActionsOpen ? 'rotate(90deg) scale(0.5)' : 'rotate(0deg) scale(1)'}
            >
              <FiMoreHorizontal size={16} />
            </Box>
            <Box
              position='absolute'
              display='flex'
              transition='transform 0.28s ease, opacity 0.2s ease'
              opacity={isActionsOpen ? 1 : 0}
              transform={isActionsOpen ? 'rotate(0deg) scale(1)' : 'rotate(-90deg) scale(0.5)'}
            >
              <FiX size={16} />
            </Box>
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );

  const renderSidebar = () => (
    <Flex
      direction='column'
      gap={4}
      {...cardStyle}
      p={4}
      w={isCompact ? '236px' : '272px'}
      h='100%'
      flexShrink={0}
      overflow='hidden'
    >
      <Box flex={1} minH={0}>
        <VersionsPanel
          dataSourceId={dataSourceId}
          sourceName={sourceName}
          onAddVersion={setVersionModal.on}
        />
      </Box>

      <Box borderTop='1px solid' borderColor={PLAN_TABLE_CELL_BORDER} pt={4} flexShrink={0}>
        <SectionTitle mb={3}>Source details</SectionTitle>
        {[
          ['File name', sourceName],
          ['Type', typeLabel],
          ['Total versions', `${versions.length}`],
          ['Last updated', formatCreatedOn(versions[0]?.date_created ?? source?.date_created)],
        ].map(([label, value]) => (
          <Box key={label} mb={3} _last={{ mb: 0 }}>
            <Text {...PLAN_TEXT.labelSm} color={colors.textSecondary}>
              {label}
            </Text>
            <Text
              fontSize={fontSize.body}
              fontWeight={500}
              color={colors.textDark}
              noOfLines={2}
              wordBreak='break-all'
              title={value}
            >
              {value}
            </Text>
          </Box>
        ))}
      </Box>
    </Flex>
  );

  const renderCreateColumn = () => (
    <Flex
      w={isMobile ? '50%' : isSmallLaptop ? '380px' : '420px'}
      flexShrink={0}
      h='100%'
      direction='column'
    >
      <ActionCreateForm setactionCreate={setActionCreate} />
    </Flex>
  );

  return (
    <Flex
      direction='column'
      flex='1'
      minH={0}
      w='full'
      px={PLAN_HEADER_HORIZONTAL_PADDING}
      pb={3}
      transformOrigin='center'
      animation={
        isExiting
          ? `${zoomOut} ${EXIT_MS}ms ease-in forwards`
          : isCreateView
          ? `${zoomIn} ${ENTER_MS}ms cubic-bezier(0.2, 0.8, 0.2, 1)`
          : undefined
      }
    >
      {renderHeader()}

      <Flex ref={bodyReference} h={`${bodyHeight}px`} w='full' align='stretch' gap={3}>
        {isCreateView ? renderCreateColumn() : renderSidebar()}

        <Flex direction='column' flex='1' minW={0} minH={0} gap={3}>
          {!isCreateView && (
            <ActionsBar dataSourceId={dataSourceId} onCreateAction={() => setActionCreate(true)} />
          )}
          <DataPreview isReadOnly={isCreateView} />
        </Flex>
      </Flex>

      <DiscardActionModal
        isOpen={isDiscardOpen}
        completedSteps={activeActionStep}
        isEditing={Boolean(isEditingAction)}
        onKeepEditing={() => {
          pendingNavigation.current = null;
          setIsDiscardOpen(false);
        }}
        onDiscard={() => {
          setIsDiscardOpen(false);
          closeCreateForm();
          // A blocked route change carries on to where the user was heading.
          const retry = pendingNavigation.current;
          pendingNavigation.current = null;
          retry?.();
        }}
      />
      <AddVersionModal setVersionModal={setVersionModal} versionModal={versionModal} />
      <EditSourceModal
        isOpen={isEditOpen}
        initialName={sourceName}
        onClose={() => setIsEditOpen(false)}
        onSave={saveName}
      />
      <DeleteModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        itemName={sourceName}
        onDelete={confirmDelete}
      />
    </Flex>
  );
};

export default SourceDetail;
